import React, { useMemo } from 'react';
import {
  Box, Typography, useTheme, alpha,
} from '@mui/material';
import { HourglassEmpty } from '@mui/icons-material';

import { AdmissionApplication, AdmissionCycle, ApplicationStatus, DetailChange, IdNamePair } from '../../../types/admissions';
import { fieldDisplayValue } from '../form/admissionFormFields';
import { humanize } from '../../../utils/pdf/format';
import StatusChip from '../../common/StatusChip';
import dateTimeUtils from '../../../utils/dateTimeUtils';

/**
 * Compact state-transition timeline. Reverse-chronological (newest first).
 * Pattern mirrors `components/students/AcademicHistoryTimeline` — the
 * connector line and dot are absolute-positioned *per entry* (not a shared
 * rail on an outer wrapper), which keeps the layout bulletproof across
 * browsers.
 *
 * Each entry = chip (title) + muted meta line (time · actor) + optional
 * italic reason when the reason adds info beyond the status label.
 */

interface Props { app: AdmissionApplication; cycle: AdmissionCycle }

const STATUS_LABEL: Partial<Record<ApplicationStatus, string>> = {
  Draft: 'Draft', Submitted: 'Submitted', AppFeePending: 'App fee pending',
  UnderReview: 'Under review', DocsRequired: 'Docs required', Ineligible: 'Ineligible',
  AssessmentScheduled: 'Assessment scheduled', Absent: 'Absent', Assessed: 'Assessed',
  Offered: 'Offered', OfferAccepted: 'Offer accepted', OfferDeclined: 'Offer declined',
  OfferLapsed: 'Offer lapsed', Waitlisted: 'Waitlisted', NotAdmitted: 'Not admitted',
  Rejected: 'Rejected', ConfirmedConditional: 'Confirmed (conditional)',
  AdmFeePending: 'Adm. fee pending', Confirmed: 'Confirmed', Enrolled: 'Enrolled',
  Withdrawn: 'Withdrawn',
};

const canon = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const isRedundantReason = (reason: string | undefined, status: ApplicationStatus): boolean => {
  if (!reason) return true;
  const label = STATUS_LABEL[status];
  if (!label) return false;
  const r = canon(reason);
  const l = canon(label);
  if (r === l || r.includes(l) || l.includes(r)) return true;
  const trivial = new Set([
    'publicsubmission', 'publicformsubmitted', 'walkinsubmission',
    'applicationfeepending', 'admissionfeepending',
  ]);
  return trivial.has(r);
};

const CandidateTimelineTab: React.FC<Props> = ({ app, cycle }) => {
  const theme = useTheme();
  // Corrections list each changed answer by its label on the form.
  const fieldByKey = useMemo(
    () => new Map((cycle.formTemplate?.sections || []).flatMap(s => s.fields).map(f => [f.key, f])),
    [cycle.formTemplate],
  );
  const classOptions = useMemo(
    () => (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object'),
    [cycle.allowedClassIds],
  );
  const describe = (c: DetailChange) => {
    const field = fieldByKey.get(c.key);
    const shown = (v: unknown) => (field ? fieldDisplayValue(field, v, classOptions) : String(v ?? '')) || 'blank';
    return { label: field?.label || humanize(c.key), from: shown(c.from), to: shown(c.to) };
  };
  const entries = [...(app.timeline || [])].reverse();

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <HourglassEmpty sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No timeline entries yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {entries.map((entry, idx) => {
        const isLatest = idx === 0;
        const isLast = idx === entries.length - 1;
        const byPerson = typeof entry.by === 'object' && entry.by
          ? (entry.by as any).name || 'Staff'
          : entry.by
            ? 'Staff'
            : 'System';
        const status = entry.to as ApplicationStatus | undefined;
        const isEvent = !status && !!entry.event;
        const showReason = !!entry.reason && (isEvent ? entry.event === 'details_corrected' : !isRedundantReason(entry.reason, status!));
        const dotColor = isLatest ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.35);

        return (
          <Box key={`${entry.at}-${idx}`} sx={{ position: 'relative', pl: 3 }}>
            {/* Connector line to the next entry */}
            {!isLast && (
              <Box sx={{
                position: 'absolute',
                left: '7px', top: '18px', bottom: '-8px',
                width: '1px',
                bgcolor: theme.palette.divider,
              }} />
            )}

            {/* Dot */}
            <Box sx={{
              position: 'absolute',
              left: '4px', top: '8px',
              width: '8px', height: '8px',
              borderRadius: '50%',
              bgcolor: isLatest ? dotColor : theme.palette.background.paper,
              border: `1.5px solid ${dotColor}`,
              boxShadow: isLatest ? `0 0 0 3px ${alpha(dotColor, 0.18)}` : 'none',
            }} />

            <Box sx={{ pb: isLast ? 0 : 2.25 }}>
              {isEvent ? (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {entry.label || entry.event}
                </Typography>
              ) : (
                <StatusChip status={{ kind: 'application', value: status! }} />
              )}
              {isEvent && Array.isArray(entry.meta?.changes) && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
                  {(entry.meta!.changes as DetailChange[]).map(c => {
                    const d = describe(c);
                    return (
                      <Typography key={c.key} component="li" variant="body2" color="text.secondary">
                        {d.label}: <Box component="span" sx={{ textDecoration: 'line-through' }}>{d.from}</Box>
                        {' → '}<Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{d.to}</Box>
                      </Typography>
                    );
                  })}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {dateTimeUtils.formatDateTimeForDisplay(entry.at)} · {byPerson}
              </Typography>
              {showReason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  {entry.reason}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CandidateTimelineTab;
